import { AccountService, ApiClient, DrawingClient, makeCallRuntime } from '@acies/platform';
import { Effect, Layer } from 'effect';
import { DevTools } from 'effect/devtools';
import { callDependencies } from './shared';

/** The `localStorage` key that turns span streaming to the DevTools extension on. */
const devToolsKey = 'effect-devtools';

/**
 * The tracer that streams spans to the Effect DevTools extension, or nothing.
 *
 * Detected at runtime rather than at build time for the same reason `EnvironmentService` detects the environment from the hostname: the module is built standalone and never sees the host app's build-time substitution, so `import.meta.env.DEV` would be baked in here — and would be `false` under `vite build --watch`,
 * which is exactly when it is wanted.
 *
 * Opt-in rather than always on in dev, because the client waits up to a second for the connection and queues spans without a bound while nothing drains them. Neither is a cost a build should pay unasked. The hostname is checked too, so the dev site and the PR previews cannot be switched into calling a visitor's own
 * machine.
 *
 * Read once, when the module loads, which is when the runtime below is assembled: turning the flag on takes a reload.
 */
export function devToolsLayer(): Layer.Layer<never> {
	return window.location.hostname === 'localhost' && localStorage.getItem(devToolsKey) !== null ? DevTools.layer() : Layer.empty;
}

/**
 * Everything the runtime provides, which is everything a call may reach for.
 *
 * Named once, so the layers below and the calls above cannot come apart: a service added here has to be added there before anything can ask for it.
 */
export type ApiServices = AccountService | ApiClient | DrawingClient;

const services: Layer.Layer<ApiServices> = Layer.mergeAll(
	AccountService.layer,
	ApiClient.layer,
	DrawingClient.layer,
	// A `Layer<never>` either way: the tracer the DevTools layer installs sits in the runtime's
	// context without appearing in its type, so `ApiServices` reads the same whether the gate is
	// open or shut. It has to be merged here rather than into `ApiClient.layer`, because this is
	// the layer `makeCallRuntime` builds the runtime from, and a tracer is only found by the spans
	// opened inside that runtime.
	devToolsLayer(),
);

/**
 * The Configurator's runtime: which services its calls run over, and what each call is handed.
 *
 * One for the module, so a call awaited through {@link runCall} and one the machine forks as a fiber share the same fetch client, account and tracer. See `makeCallRuntime` for the convention it carries.
 */
export const callRuntime = makeCallRuntime(services, callDependencies);

/**
 * Runs one of the Configurator's calls from outside Effect.
 *
 * Everything about how a failure crosses the boundary — answered rather than thrown, with defects left to reject — is `makeCallRuntime`'s, so it reads the same in every module.
 *
 * Callers that can act on the error channel itself, or that have to interrupt a call that has been overtaken, should stay in Effect: the calls have Effect forms for that, which run in {@link callRuntime}.
 */
export const runCall = callRuntime.runCall;

/**
 * `call` over the services {@link callRuntime} provides, as an Effect that needs none.
 *
 * For a component that forks its calls into a FiberHandle itself, with `Effect.runFork`, rather than through the runtime. The runtime forks a call only once it has been built, so on the first call the fiber is not in its handle yet when the fork returns — and a `clear` made straight after has nothing to interrupt,
 * while the call goes on to land. Forked in place, the fiber is in its handle as soon as the fork returns, and reaches for the runtime — building it, the first time — as its first step. Clearing the handle needs no services at all, so it never builds the runtime for nothing.
 */
export const provideCallServices = <A, E>(call: Effect.Effect<A, E, ApiServices>): Effect.Effect<A, E> => callRuntime.runtime.contextEffect.pipe(Effect.flatMap((context) => Effect.provide(call, context)));
